import type {
  ParticipantEditFormValues,
  ParticipantEditMode,
  ParticipantEditPhoneFormSlot,
  ParticipantEditRule,
  ParticipantPhoneType,
} from '../../domain/types'
import type {
  ParticipantEditAddressPayload,
  ParticipantEditPayload,
  ParticipantEditPhonePayload,
} from '../types'
import { format } from 'date-fns'
import { parseDate } from '@/scopes/account/common/utils/bookingDates'
import { isExternallyEditable } from '../../domain/participantEditRules'

export function participantEditPayloadMapper(
  values: ParticipantEditFormValues,
  rules: ParticipantEditRule[],
  mode: ParticipantEditMode = 'standard',
): ParticipantEditPayload {
  const offeredRules = mode === 'externalRequest'
    ? rules.filter(isExternallyEditable)
    : rules

  const address = mapAddress(values, offeredRules)
  const phones = mapPhones(values, offeredRules)

  return {
    ...pickField(offeredRules, 'title', values.title),
    ...pickField(offeredRules, 'gender', values.gender),
    ...pickField(offeredRules, 'firstName', values.firstName),
    ...pickField(offeredRules, 'lastName', values.lastName),
    ...pickField(offeredRules, 'dateOfBirth', toApiDate(values.dateOfBirth)),
    ...pickField(offeredRules, 'email', values.email),
    ...(address ? { address } : {}),
    ...(phones.length ? { phones } : {}),
    isExternalForm: mode === 'externalRequest',
  }
}

type ParticipantEditTextField = keyof Pick<
  ParticipantEditPayload,
  'title' | 'gender' | 'firstName' | 'lastName' | 'dateOfBirth' | 'email'
>

function isOffered(rules: ParticipantEditRule[], fieldName: string, subType?: string): boolean {
  const rule = rules.find(rule => rule.fieldName === fieldName)
  return rule?.configurations.some(configuration => configuration.subType === subType) ?? false
}

function pickField(
  rules: ParticipantEditRule[],
  fieldName: ParticipantEditTextField,
  value: string,
): Partial<ParticipantEditPayload> {
  return isOffered(rules, fieldName) ? { [fieldName]: value } : {}
}

function pickAddressField(rules: ParticipantEditRule[], subField: keyof ParticipantEditAddressPayload, value: string) {
  return isOffered(rules, 'address', subField) ? { [subField]: value } : {}
}

function mapAddress(
  values: ParticipantEditFormValues,
  rules: ParticipantEditRule[],
): ParticipantEditAddressPayload | undefined {
  const address = {
    ...pickAddressField(rules, 'street', values.street),
    ...pickAddressField(rules, 'houseNumber', values.houseNumber),
    ...pickAddressField(rules, 'houseNumberAddition', values.houseNumberAddition),
    ...pickAddressField(rules, 'postalCode', values.postalCode),
    ...pickAddressField(rules, 'city', values.city),
    ...pickAddressField(rules, 'countryCode', values.countryCode),
  }

  return Object.keys(address).length ? address : undefined
}

function mapPhones(
  values: ParticipantEditFormValues,
  rules: ParticipantEditRule[],
): ParticipantEditPhonePayload[] {
  return [
    ...mapPhonesOfType(rules, 'Mobile', values.mobilePhoneNumbers),
    ...mapPhonesOfType(rules, 'Emergency', values.emergencyPhoneNumbers),
  ]
}

function mapPhonesOfType(
  rules: ParticipantEditRule[],
  subType: ParticipantPhoneType,
  phones: ParticipantEditPhoneFormSlot[],
): ParticipantEditPhonePayload[] {
  if (!isOffered(rules, 'phones', subType)) {
    return []
  }

  return phones.map(phone => ({ type: subType.toLowerCase(), number: toApiPhoneNumber(phone) }))
}

function toApiDate(dateValue: Date | string | undefined): string {
  const parsed = dateValue ? parseDate(dateValue) : null
  return parsed ? format(parsed, 'yyyy-MM-dd') : ''
}

function toApiPhoneNumber(phone: ParticipantEditPhoneFormSlot): string {
  if (!phone) {
    return ''
  }

  const normalizedNumber = phone.phoneNumber.replace(/\s/g, '')

  return normalizedNumber ? `+${phone.countryCode}${normalizedNumber}` : ''
}
